// The kiosk app ("the shell") hands the page its tokens through window.FleetShell,
// an Android addJavascriptInterface bridge. Every method is synchronous. The
// device secret never reaches JavaScript; only the short-lived device token does.

export interface FleetShellBridge {
  /** Current device token, '' when not enrolled or not yet fetched. */
  deviceToken(): string;
  isEnrolled(): boolean;
  /** The 4-hex scanner ID from the Scanners page, '' before enrollment. */
  scannerId(): string;
  /** The optional label it was enrolled under, '' when none. */
  deviceName(): string;
  instance(): 'firefleet' | 'swarm';
  /** Tells the shell whether anyone is signed in; app updates install only while idle. Older shells lack it. */
  setIdle?(idle: boolean): void;
  /** JSON: {"battery": 0-100|null, "charging": bool, "wifi": 0-4|null}. Older shells lack it. */
  status?(): string;
  /** Vibrates for the server's verdict on a scan. Older shells lack it. */
  buzz?(kind: BuzzKind): void;
  version(): string;
}

export type BuzzKind = 'reject' | 'warn';

declare global {
  interface Window {
    FleetShell?: FleetShellBridge;
  }
}

/** True only inside the kiosk WebView. Browsers and phones never have the bridge. */
export function inShell(): boolean {
  return typeof window !== 'undefined' && !!window.FleetShell;
}

export function getShell(): FleetShellBridge | null {
  return typeof window !== 'undefined' ? window.FleetShell ?? null : null;
}

/** The bridge can throw if the shell is mid-teardown; callers want '' then. */
export function readDeviceToken(): string {
  try {
    return getShell()?.deviceToken() || '';
  } catch {
    return '';
  }
}

/** The scanner's ID, '' outside the shell or before enrollment. */
export function readScannerId(): string {
  try {
    return getShell()?.scannerId() || '';
  } catch {
    return '';
  }
}

/** Felt feedback for a refused or questionable scan. A no-op outside the shell or on an older one. */
export function buzz(kind: BuzzKind): void {
  try {
    getShell()?.buzz?.(kind);
  } catch {
    // The bridge can throw mid-teardown; a missed buzz is harmless.
  }
}
